import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Issue, IssueForm, IssueState, KanbanData, KanbanMeta, ApiResponse, PaginatedResponse } from '../../types'
import api, { uploadIssueImages, deleteIssueImage } from '../../services/api'

const initialState: IssueState = {
  issues: [],
  currentIssue: null,
  kanbanData: null,
  kanbanMeta: null,
  labels: [],
  loading: false,
  error: null,
}

// Async thunks
export const fetchIssues = createAsyncThunk(
  'issues/fetchIssues',
  async (params: {
    project: string
    page?: number
    limit?: number
    search?: string
    status?: string
    type?: string
    priority?: string
    assignee?: string
    reporter?: string
    sortBy?: string
    sortOrder?: string
  }, { rejectWithValue }) => {
    try {
      const response = await api.get<PaginatedResponse<Issue>>('/issues', { params })
      return response.data.data.issues
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch issues')
    }
  }
)

export const fetchKanbanData = createAsyncThunk(
  'issues/fetchKanbanData',
  async (
    { projectId, includeCompleted = true }: { projectId: string; includeCompleted?: boolean },
    { rejectWithValue }
  ) => {
    try {
      if (!projectId) {
        return rejectWithValue('Project ID is required')
      }
      
      // Add retry mechanism
      let lastError: any
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await api.get<ApiResponse<{ kanban: KanbanData; meta?: KanbanMeta }>>(
            `/issues/kanban/${projectId}`,
            { params: { includeCompleted } }
          )
          return response.data.data!
        } catch (error: any) {
          lastError = error
          if (attempt < 3) {
            // Wait before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, attempt * 1000))
            continue
          }
        }
      }
      
      return rejectWithValue(lastError?.response?.data?.message || 'Failed to fetch kanban data after retries')
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch kanban data')
    }
  }
)

export const fetchIssue = createAsyncThunk(
  'issues/fetchIssue',
  async (issueId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<{ issue: Issue }>>(`/issues/${issueId}`)
      return response.data.data!.issue
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch issue')
    }
  }
)

/** Fetches full issue in the background without setting global loading (e.g. when opening detail from Kanban with slim issue). */
export const fetchIssueForDetail = createAsyncThunk(
  'issues/fetchIssueForDetail',
  async (issueId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<{ issue: Issue }>>(`/issues/${issueId}`)
      return response.data.data!.issue
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch issue')
    }
  }
)

export const fetchIssueByKey = createAsyncThunk(
  'issues/fetchIssueByKey',
  async (issueKey: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<{ issue: Issue }>>(`/issues/key/${issueKey}`)
      return response.data.data!.issue
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch issue')
    }
  }
)

export const createIssue = createAsyncThunk(
  'issues/createIssue',
  async (issueData: IssueForm, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<{ issue: Issue }>>('/issues', issueData)
      return response.data.data!.issue
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create issue')
    }
  }
)

export const updateIssue = createAsyncThunk(
  'issues/updateIssue',
  async ({ issueId, issueData }: { issueId: string; issueData: Partial<IssueForm> }, { rejectWithValue }) => {
    try {
      const response = await api.put<ApiResponse<{ issue: Issue }>>(`/issues/${issueId}`, issueData)
      return response.data.data!.issue
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update issue')
    }
  }
)

export const updateIssueStatus = createAsyncThunk(
  'issues/updateIssueStatus',
  async ({ issueId, status }: { issueId: string; status: any }, { rejectWithValue }) => {
    try {
      const response = await api.patch<ApiResponse<{ issue: Partial<Issue> }>>(`/issues/${issueId}/status`, { status })
      return { issueId, status, updatedIssue: response.data.data!.issue }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update issue status')
    }
  }
)

export const deleteIssue = createAsyncThunk(
  'issues/deleteIssue',
  async (issueId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/issues/${issueId}`)
      return issueId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete issue')
    }
  }
)

export const addComment = createAsyncThunk(
  'issues/addComment',
  async ({ issueId, content }: { issueId: string; content: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<ApiResponse<{ comment: any }>>(`/issues/${issueId}/comments`, { content })
      return { issueId, comment: response.data.data!.comment }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add comment')
    }
  }
)

export const fetchMyBoardData = createAsyncThunk(
  'issues/fetchMyBoardData',
  async (
    { includeCompleted = true, userId }: { includeCompleted?: boolean; userId?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      const params: { includeCompleted: boolean; userId?: string } = { includeCompleted }
      if (userId) params.userId = userId
      const response = await api.get<ApiResponse<{ issues: Issue[]; kanban: KanbanData; meta?: KanbanMeta }>>(
        '/issues/my-board',
        { params }
      )
      return response.data.data!
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch my board data')
    }
  }
)

// Track latest my-board request so slower responses don't overwrite newer selections
let latestMyBoardRequestId: string | null = null

export const uploadImages = createAsyncThunk(
  'issues/uploadImages',
  async ({ issueId, images }: { issueId: string; images: File[] }, { rejectWithValue }) => {
    try {
      const response = await uploadIssueImages(issueId, images)
      // uploadIssueImages already returns the body { success, message, data }
      return { issueId, images: response.data.images }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload images')
    }
  }
)

export const removeImage = createAsyncThunk(
  'issues/removeImage',
  async ({ issueId, imageId }: { issueId: string; imageId: string }, { rejectWithValue }) => {
    try {
      await deleteIssueImage(issueId, imageId)
      return { issueId, imageId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete image')
    }
  }
)

export const fetchLabels = createAsyncThunk(
  'issues/fetchLabels',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<ApiResponse<{ labels: string[] }>>(`/issues/labels/${projectId}`)
      return response.data.data!.labels
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch labels')
    }
  }
)

const issueSlice = createSlice({
  name: 'issues',
  initialState,
  reducers: {
    clearCurrentIssue: (state) => {
      state.currentIssue = null
    },
    clearError: (state) => {
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    updateIssueInList: (state, action: PayloadAction<Issue>) => {
      const index = state.issues.findIndex(i => i._id === action.payload._id)
      if (index !== -1) {
        state.issues[index] = action.payload
      }
    },
    updateIssueTimeTracking: (state, action: PayloadAction<{
      issueId: string
      timeSpent: number
    }>) => {
      const { issueId, timeSpent } = action.payload
      
      // Update current issue
      if (state.currentIssue && state.currentIssue._id === issueId) {
        if (!state.currentIssue.timeTracking) {
          state.currentIssue.timeTracking = { timeSpent: 0 }
        }
        state.currentIssue.timeTracking.timeSpent = timeSpent
      }
      
      // Update in issues list
      const issueIndex = state.issues.findIndex(i => i._id === issueId)
      if (issueIndex !== -1) {
        if (!state.issues[issueIndex].timeTracking) {
          state.issues[issueIndex].timeTracking = { timeSpent: 0 }
        }
        state.issues[issueIndex].timeTracking!.timeSpent = timeSpent
      }
      
      // Update in kanban data
      if (state.kanbanData) {
        Object.keys(state.kanbanData).forEach(column => {
          const col = column as keyof KanbanData
          const kanbanIndex = state.kanbanData![col].findIndex(i => i._id === issueId)
          if (kanbanIndex !== -1) {
            if (!state.kanbanData![col][kanbanIndex].timeTracking) {
              state.kanbanData![col][kanbanIndex].timeTracking = { timeSpent: 0 }
            }
            state.kanbanData![col][kanbanIndex].timeTracking!.timeSpent = timeSpent
          }
        })
      }
    },
    // Real-time update: Add comment to issue (dedupe by comment._id so socket re-emit doesn't duplicate)
    addCommentToIssue: (state, action: PayloadAction<{
      issueId: string
      comment: any
    }>) => {
      const { issueId, comment } = action.payload
      const commentId = comment?._id

      const pushIfNew = (comments: any[]) => {
        if (!commentId || comments.some((c: any) => c._id === commentId)) return
        comments.push(comment)
      }

      // Update current issue
      if (state.currentIssue && state.currentIssue._id === issueId) {
        if (!state.currentIssue.comments) {
          state.currentIssue.comments = []
        }
        pushIfNew(state.currentIssue.comments)
      }

      // Update in issues list
      const issueIndex = state.issues.findIndex(i => i._id === issueId)
      if (issueIndex !== -1) {
        if (!state.issues[issueIndex].comments) {
          state.issues[issueIndex].comments = []
        }
        pushIfNew(state.issues[issueIndex].comments!)
      }

      // Update in kanban data
      if (state.kanbanData) {
        Object.keys(state.kanbanData).forEach(column => {
          const col = column as keyof KanbanData
          const kanbanIndex = state.kanbanData![col].findIndex(i => i._id === issueId)
          if (kanbanIndex !== -1) {
            if (!state.kanbanData![col][kanbanIndex].comments) {
              state.kanbanData![col][kanbanIndex].comments = []
            }
            pushIfNew(state.kanbanData![col][kanbanIndex].comments!)
          }
        })
      }
    },
    // Real-time update: Update issue in kanban (from socket)
    updateIssueInKanban: (state, action: PayloadAction<Issue>) => {
      const updatedIssue = action.payload
      if (!state.kanbanData) return
      
      // Get the new status category - early return if invalid
      const newCategory = updatedIssue.status?.category as keyof KanbanData | undefined
      if (!newCategory || !state.kanbanData[newCategory]) return
      
      // Find the issue in all columns - optimized to break early
      const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
      let foundInColumn: keyof KanbanData | null = null
      let foundIndex = -1
      
      for (const column of columns) {
        const index = state.kanbanData[column].findIndex(i => i._id === updatedIssue._id)
        if (index !== -1) {
          foundInColumn = column
          foundIndex = index
          break
        }
      }
      
      // Move issue between columns if status changed, or update in place
      if (foundInColumn !== null && foundIndex !== -1) {
        // Remove from old column
        state.kanbanData[foundInColumn].splice(foundIndex, 1)
        
        // Add to new column (or back to same column if status didn't change category)
        state.kanbanData[newCategory].push(updatedIssue)
      } else {
        // Issue not found in any column, add to the correct column based on status
        state.kanbanData[newCategory].push(updatedIssue)
      }
      
      // Update in issues list
      const issueIndex = state.issues.findIndex(i => i._id === updatedIssue._id)
      if (issueIndex !== -1) {
        state.issues[issueIndex] = updatedIssue
      }
      
      // Update current issue if it matches
      if (state.currentIssue && state.currentIssue._id === updatedIssue._id) {
        state.currentIssue = updatedIssue
      }
    },
    // Real-time update: Remove issue from kanban (deleted)
    removeIssueFromKanban: (state, action: PayloadAction<string>) => {
      const issueId = action.payload
      if (!state.kanbanData) return
      
      // Remove from all kanban columns
      const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
      for (const column of columns) {
        state.kanbanData[column] = state.kanbanData[column].filter(i => i._id !== issueId)
      }
      
      // Remove from issues list
      state.issues = state.issues.filter(i => i._id !== issueId)
      
      // Clear current issue if it matches
      if (state.currentIssue && state.currentIssue._id === issueId) {
        state.currentIssue = null
      }
    },
    moveIssueInKanban: (state, action: PayloadAction<{
      issueId: string
      fromColumn: keyof KanbanData
      toColumn: keyof KanbanData
      fromIndex: number
      toIndex: number
    }>) => {
      if (!state.kanbanData) return

      const { issueId, fromColumn, toColumn, fromIndex, toIndex } = action.payload

      // Remove from source column
      const [movedIssue] = state.kanbanData[fromColumn].splice(fromIndex, 1)

      if (!movedIssue) return

      // Update issue status based on target column
      const statusMap: Record<keyof KanbanData, { name: string; category: keyof KanbanData; color: string }> = {
        backlog: { name: 'Backlog', category: 'backlog', color: '#9ca3af' },
        todo: { name: 'To Do', category: 'todo', color: '#6b7280' },
        in_progress: { name: 'In Progress', category: 'in_progress', color: '#3b82f6' },
        qa_ready: { name: 'QA Ready', category: 'qa_ready', color: '#f59e0b' },
        qa_in_progress: { name: 'QA In Progress', category: 'qa_in_progress', color: '#fbbf24' },
        qa_done: { name: 'QA Done', category: 'qa_done', color: '#34d399' },
        live: { name: 'Live', category: 'live', color: '#8b5cf6' },
        qa_live: { name: 'QA Live', category: 'qa_live', color: '#6366f1' },
        completed: { name: 'Completed', category: 'completed', color: '#10b981' }
      }

      const newStatus = statusMap[toColumn]
      movedIssue.status = newStatus

      // Add to target column
      state.kanbanData[toColumn].splice(toIndex, 0, movedIssue)

      // Update in issues list as well
      const issueIndex = state.issues.findIndex(i => i._id === issueId)
      if (issueIndex !== -1) {
        state.issues[issueIndex].status = newStatus as any
      }

      // If this is a linked task (has parent), update it in the parent's subtasks array
      // Get parentId from movedIssue, or from issues list if parent field is not populated
      let parentId = typeof movedIssue.parent === 'string' ? movedIssue.parent : movedIssue.parent?._id
      if (!parentId) {
        // Try to get parent from issues list
        const issueFromList = state.issues.find(i => i._id === issueId)
        if (issueFromList) {
          parentId = typeof issueFromList.parent === 'string' ? issueFromList.parent : issueFromList.parent?._id
        }
      }
      if (parentId) {
        // Update in current issue if it's the parent
        if (state.currentIssue && state.currentIssue._id === parentId && state.currentIssue.subtasks) {
          const subtaskIndex = state.currentIssue.subtasks.findIndex(s => s._id === issueId)
          if (subtaskIndex !== -1) {
            state.currentIssue.subtasks[subtaskIndex].status = newStatus as any
          }
        }
        
        // Update in issues list if parent exists
        const parentIndex = state.issues.findIndex(i => i._id === parentId)
        if (parentIndex !== -1 && state.issues[parentIndex].subtasks) {
          const subtaskIndex = state.issues[parentIndex].subtasks.findIndex(s => s._id === issueId)
          if (subtaskIndex !== -1) {
            state.issues[parentIndex].subtasks[subtaskIndex].status = newStatus as any
          }
        }
        
        // Update in kanban data if parent exists
        const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
        for (const column of columns) {
          const parentIndex = state.kanbanData[column].findIndex(i => i._id === parentId)
          if (parentIndex !== -1 && state.kanbanData[column][parentIndex].subtasks) {
            const subtaskIndex = state.kanbanData[column][parentIndex].subtasks.findIndex(s => s._id === issueId)
            if (subtaskIndex !== -1) {
              state.kanbanData[column][parentIndex].subtasks[subtaskIndex].status = newStatus as any
            }
            break
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Issues
    builder
      .addCase(fetchIssues.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIssues.fulfilled, (state, action) => {
        state.loading = false
        state.issues = action.payload
        state.error = null
      })
      .addCase(fetchIssues.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Kanban Data
    builder
      .addCase(fetchKanbanData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchKanbanData.fulfilled, (state, action) => {
        state.loading = false
        state.kanbanData = action.payload.kanban
        
        // If meta is provided, use it. Otherwise, infer from the request and data
        const meta = action.payload.meta
        if (meta) {
          state.kanbanMeta = meta
          // If includeCompleted is false but completed array has items, clear it (stale data)
          if (!meta.includeCompleted && state.kanbanData.completed.length > 0) {
            state.kanbanData.completed = []
          }
        } else {
          // Fallback: infer from completed array length
          // If completed array is empty, assume includeCompleted was false
          const hasCompleted = action.payload.kanban.completed.length > 0
          state.kanbanMeta = {
            includeCompleted: hasCompleted,
            completedCount: hasCompleted ? action.payload.kanban.completed.length : 0
          }
        }
        state.error = null
      })
      .addCase(fetchKanbanData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Issue
    builder
      .addCase(fetchIssue.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIssueByKey.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchIssue.fulfilled, (state, action) => {
        state.loading = false
        const fetchedIssue = action.payload
        
        // Update currentIssue
        state.currentIssue = fetchedIssue
        
        // Update in issues list if it exists
        const issueIndex = state.issues.findIndex(i => i._id === fetchedIssue._id)
        if (issueIndex !== -1) {
          state.issues[issueIndex] = fetchedIssue
        }
        
        // Update in kanban data if it exists
        if (state.kanbanData) {
          const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
          for (const column of columns) {
            const kanbanIndex = state.kanbanData[column].findIndex(i => i._id === fetchedIssue._id)
            if (kanbanIndex !== -1) {
              state.kanbanData[column][kanbanIndex] = fetchedIssue
              break
            }
          }
        }
        
        state.error = null
      })
      .addCase(fetchIssue.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(fetchIssueByKey.fulfilled, (state, action) => {
        state.loading = false
        const fetchedIssue = action.payload
        
        // Update currentIssue
        state.currentIssue = fetchedIssue
        
        // Update in issues list if it exists
        const issueIndex = state.issues.findIndex(i => i._id === fetchedIssue._id)
        if (issueIndex !== -1) {
          state.issues[issueIndex] = fetchedIssue
        }
        
        // Update in kanban data if it exists
        if (state.kanbanData) {
          const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
          for (const column of columns) {
            const kanbanIndex = state.kanbanData[column].findIndex(i => i._id === fetchedIssue._id)
            if (kanbanIndex !== -1) {
              state.kanbanData[column][kanbanIndex] = fetchedIssue
              break
            }
          }
        }
        
        state.error = null
      })
      .addCase(fetchIssueByKey.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      // fetchIssueForDetail: same as fetchIssue but does not set loading (background enrich for modal)
      .addCase(fetchIssueForDetail.fulfilled, (state, action) => {
        const fetchedIssue = action.payload
        state.currentIssue = fetchedIssue
        const issueIndex = state.issues.findIndex(i => i._id === fetchedIssue._id)
        if (issueIndex !== -1) {
          state.issues[issueIndex] = fetchedIssue
        }
        if (state.kanbanData) {
          const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
          for (const column of columns) {
            const kanbanIndex = state.kanbanData[column].findIndex(i => i._id === fetchedIssue._id)
            if (kanbanIndex !== -1) {
              state.kanbanData[column][kanbanIndex] = fetchedIssue
              break
            }
          }
        }
      })

    // Create Issue
    builder
      .addCase(createIssue.pending, (state) => {
        // Don't set loading = true for creates to prevent UI flicker
        // The state will be updated optimistically via the fulfilled case
        state.error = null
      })
      .addCase(createIssue.fulfilled, (state, action) => {
        // Don't set loading = false here since we never set it to true
        // This prevents the loading spinner from showing during creation
        const newIssue = action.payload
        
        // If this is a linked task and it's the current issue being viewed, update currentIssue
        // This ensures the parent information is immediately available
        if (state.currentIssue && state.currentIssue._id === newIssue._id) {
          state.currentIssue = newIssue
        }
        
        // Add issue to issues list and kanban board (linked tasks behave like normal tasks)
        state.issues.unshift(newIssue)
        
        // Add to kanban data if it exists
        if (state.kanbanData) {
          const category = newIssue.status.category as keyof KanbanData
          state.kanbanData[category].unshift(newIssue)
        }
        
        // If this is a linked task (has parent), also update the parent issue
        const parentId = typeof newIssue.parent === 'string' ? newIssue.parent : newIssue.parent?._id
        if (parentId) {
          // Update the parent issue in the current issue if it matches
          if (state.currentIssue && state.currentIssue._id === parentId) {
            if (!state.currentIssue.subtasks) {
              state.currentIssue.subtasks = []
            }
            state.currentIssue.subtasks.push(newIssue)
          }
          
          // Also update parent in issues list if it exists
          const parentIndex = state.issues.findIndex(i => i._id === parentId)
          if (parentIndex !== -1) {
            if (!state.issues[parentIndex].subtasks) {
              state.issues[parentIndex].subtasks = []
            }
            state.issues[parentIndex].subtasks.push(newIssue)
          }
          
          // Update parent in kanban data if it exists
          if (state.kanbanData) {
            const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
            for (const column of columns) {
              const parentIndex = state.kanbanData[column].findIndex(i => i._id === parentId)
              if (parentIndex !== -1) {
                if (!state.kanbanData[column][parentIndex].subtasks) {
                  state.kanbanData[column][parentIndex].subtasks = []
                }
                state.kanbanData[column][parentIndex].subtasks.push(newIssue)
                break
              }
            }
          }
        }
        
        state.error = null
      })
      .addCase(createIssue.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update Issue
    builder
      .addCase(updateIssue.pending, (state) => {
        // Don't set loading = true for updates to prevent UI flicker
        // The state will be updated optimistically via the fulfilled case
        state.error = null
      })
      .addCase(updateIssue.fulfilled, (state, action) => {
        // Don't set loading = false here since we never set it to true
        // This prevents the loading spinner from showing during updates
        
        const updatedIssue = action.payload
        const newCategory = updatedIssue.status.category as keyof KanbanData
        
        // Update in issues list
        const index = state.issues.findIndex(i => i._id === updatedIssue._id)
        if (index !== -1) {
          state.issues[index] = updatedIssue
        }
        
        // Update current issue
        if (state.currentIssue && state.currentIssue._id === updatedIssue._id) {
          state.currentIssue = updatedIssue
        }
        
        // If this is a linked task (has parent), update it in the parent's subtasks array
        const parentId = typeof updatedIssue.parent === 'string' ? updatedIssue.parent : updatedIssue.parent?._id
        if (parentId) {
          // Update in current issue if it's the parent
          if (state.currentIssue && state.currentIssue._id === parentId && state.currentIssue.subtasks) {
            const subtaskIndex = state.currentIssue.subtasks.findIndex(s => s._id === updatedIssue._id)
            if (subtaskIndex !== -1) {
              state.currentIssue.subtasks[subtaskIndex] = updatedIssue
            }
          }
          
          // Update in issues list if parent exists
          const parentIndex = state.issues.findIndex(i => i._id === parentId)
          if (parentIndex !== -1 && state.issues[parentIndex].subtasks) {
            const subtaskIndex = state.issues[parentIndex].subtasks.findIndex(s => s._id === updatedIssue._id)
            if (subtaskIndex !== -1) {
              state.issues[parentIndex].subtasks[subtaskIndex] = updatedIssue
            }
          }
          
          // Update in kanban data if parent exists
          if (state.kanbanData) {
            const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
            for (const column of columns) {
              const parentIndex = state.kanbanData[column].findIndex(i => i._id === parentId)
              if (parentIndex !== -1 && state.kanbanData[column][parentIndex].subtasks) {
                const subtaskIndex = state.kanbanData[column][parentIndex].subtasks.findIndex(s => s._id === updatedIssue._id)
                if (subtaskIndex !== -1) {
                  state.kanbanData[column][parentIndex].subtasks[subtaskIndex] = updatedIssue
                }
                break
              }
            }
          }
        }
        
        // Update in kanban data - move issue if status category changed
        if (state.kanbanData && newCategory) {
          const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
          let foundInColumn: keyof KanbanData | null = null
          let foundIndex = -1
          
          // Find the issue in the current column
          for (const column of columns) {
            const issueIndex = state.kanbanData[column].findIndex(i => i._id === updatedIssue._id)
            if (issueIndex !== -1) {
              foundInColumn = column
              foundIndex = issueIndex
              break
            }
          }
          
          // If status category changed, move the issue to the new column
          if (foundInColumn && foundInColumn !== newCategory) {
            // Remove from old column
            const [movedIssue] = state.kanbanData[foundInColumn].splice(foundIndex, 1)
            if (movedIssue) {
              // Update issue with new data and add to new column
              const updatedIssueCopy = { ...updatedIssue }
              state.kanbanData[newCategory].push(updatedIssueCopy)
            }
          } else if (foundInColumn && foundInColumn === newCategory) {
            // Status category didn't change, just update in place
            state.kanbanData[newCategory][foundIndex] = updatedIssue
          } else if (!foundInColumn) {
            // Issue not found in any column, add it to the new category
            state.kanbanData[newCategory].push(updatedIssue)
          }
        }
        
        state.error = null
      })
      .addCase(updateIssue.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update Issue Status
    builder
      .addCase(updateIssueStatus.pending, (state) => {
        state.error = null
      })
      .addCase(updateIssueStatus.fulfilled, (state, action) => {
        const { issueId, status } = action.payload
        
        // Get the issue before updating to check for parent
        const issueIndex = state.issues.findIndex(i => i._id === issueId)
        const issueBeforeUpdate = issueIndex !== -1 ? state.issues[issueIndex] : null
        const currentIssueBeforeUpdate = state.currentIssue && state.currentIssue._id === issueId ? state.currentIssue : null
        
        // Get parentId from the issue before updating
        const issueToCheck = issueBeforeUpdate || currentIssueBeforeUpdate
        const parentId = issueToCheck ? (typeof issueToCheck.parent === 'string' ? issueToCheck.parent : issueToCheck.parent?._id) : null
        
        // Update in issues list
        if (issueIndex !== -1) {
          state.issues[issueIndex].status = status
        }
        
        // Update current issue
        if (state.currentIssue && state.currentIssue._id === issueId) {
          state.currentIssue.status = status
        }
        
        // If this is a linked task (has parent), update it in the parent's subtasks array
        if (parentId) {
            // Update in current issue if it's the parent
            if (state.currentIssue && state.currentIssue._id === parentId && state.currentIssue.subtasks) {
              const subtaskIndex = state.currentIssue.subtasks.findIndex(s => s._id === issueId)
              if (subtaskIndex !== -1) {
                state.currentIssue.subtasks[subtaskIndex].status = status
              }
            }
            
            // Update in issues list if parent exists
            const parentIndex = state.issues.findIndex(i => i._id === parentId)
            if (parentIndex !== -1 && state.issues[parentIndex].subtasks) {
              const subtaskIndex = state.issues[parentIndex].subtasks.findIndex(s => s._id === issueId)
              if (subtaskIndex !== -1) {
                state.issues[parentIndex].subtasks[subtaskIndex].status = status
              }
            }
            
            // Update in kanban data if parent exists
            if (state.kanbanData) {
              const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
              for (const column of columns) {
                const parentIndex = state.kanbanData[column].findIndex(i => i._id === parentId)
                if (parentIndex !== -1 && state.kanbanData[column][parentIndex].subtasks) {
                  const subtaskIndex = state.kanbanData[column][parentIndex].subtasks.findIndex(s => s._id === issueId)
                  if (subtaskIndex !== -1) {
                    state.kanbanData[column][parentIndex].subtasks[subtaskIndex].status = status
                  }
                  break
                }
              }
            }
        }
        
        // Update in kanban data - move issue from old column to new column
        // Note: This might be called after an optimistic update from moveIssueInKanban,
        // so we need to check if the issue is already in the correct column
        if (state.kanbanData && status.category) {
          const category = status.category as keyof KanbanData
          
          // Find and remove issue from all columns
          let foundIssue: Issue | null = null
          const columns: (keyof KanbanData)[] = ['backlog', 'todo', 'in_progress', 'qa_ready', 'qa_in_progress', 'qa_done', 'live', 'qa_live', 'completed']
          
          for (const column of columns) {
            const issueIndex = state.kanbanData[column].findIndex(i => i._id === issueId)
            if (issueIndex !== -1) {
              foundIssue = { ...state.kanbanData[column][issueIndex] } as Issue
              state.kanbanData[column].splice(issueIndex, 1)
              break
            }
          }
          
          // Add issue to new column with updated status
          // Only add if we found it and it's not already in the target column (due to optimistic update)
          if (foundIssue) {
            foundIssue.status = status
            // Only add if it's not already in the target column (avoid duplicates)
            const alreadyInTarget = state.kanbanData[category].some(i => i._id === issueId)
            if (!alreadyInTarget) {
              state.kanbanData[category].push(foundIssue)
            } else {
              // Update in place if already there (from optimistic update)
              const targetIndex = state.kanbanData[category].findIndex(i => i._id === issueId)
              if (targetIndex !== -1) {
                state.kanbanData[category][targetIndex] = foundIssue
              }
              // If we removed from a different column, we already spliced it, which is correct
            }
          }
        }
        
        state.error = null
      })
      .addCase(updateIssueStatus.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Delete Issue
    builder
      .addCase(deleteIssue.pending, (state) => {
        // Don't set loading = true for deletes to prevent UI flicker
        // The state will be updated optimistically via the fulfilled case
        state.error = null
      })
      .addCase(deleteIssue.fulfilled, (state, action) => {
        // Don't set loading = false here since we never set it to true
        // This prevents the loading spinner from showing during deletes
        
        // Remove from issues list
        state.issues = state.issues.filter(i => i._id !== action.payload)
        
        // Remove from kanban data
        if (state.kanbanData) {
          Object.keys(state.kanbanData).forEach(column => {
            const col = column as keyof KanbanData
            state.kanbanData![col] = state.kanbanData![col].filter(i => i._id !== action.payload)
          })
        }
        
        // Clear current issue if it's the deleted one
        if (state.currentIssue && state.currentIssue._id === action.payload) {
          state.currentIssue = null
        }
        
        state.error = null
      })
      .addCase(deleteIssue.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Add Comment
    builder
      .addCase(addComment.pending, (state) => {
        state.error = null
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { issueId, comment } = action.payload
        
        // Add comment to current issue (slim issues from kanban may have no comments array)
        if (state.currentIssue && state.currentIssue._id === issueId) {
          if (!state.currentIssue.comments) state.currentIssue.comments = []
          state.currentIssue.comments.push(comment)
        }
        
        // Add comment to issue in issues list
        const issueIndex = state.issues.findIndex(i => i._id === issueId)
        if (issueIndex !== -1) {
          if (!state.issues[issueIndex].comments) state.issues[issueIndex].comments = []
          state.issues[issueIndex].comments.push(comment)
        }
        
        // Add comment to issue in kanban data (slim issues from kanban have no comments array)
        if (state.kanbanData) {
          Object.keys(state.kanbanData).forEach(column => {
            const col = column as keyof KanbanData
            const kanbanIndex = state.kanbanData![col].findIndex(i => i._id === issueId)
            if (kanbanIndex !== -1) {
              const issue = state.kanbanData![col][kanbanIndex]
              if (!issue.comments) issue.comments = []
              issue.comments.push(comment)
            }
          })
        }
        
        state.error = null
      })
      .addCase(addComment.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch My Board Data
    builder
      .addCase(fetchMyBoardData.pending, (state, action) => {
        latestMyBoardRequestId = action.meta.requestId
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyBoardData.fulfilled, (state, action) => {
        // Ignore outdated responses when user switches quickly
        if (action.meta.requestId !== latestMyBoardRequestId) {
          return
        }
        state.loading = false
        state.issues = action.payload.issues
        state.kanbanData = action.payload.kanban
        
        // If meta is provided, use it. Otherwise, infer from the request and data
        const meta = action.payload.meta
        if (meta) {
          state.kanbanMeta = meta
          // If includeCompleted is false but completed array has items, clear it (stale data)
          if (!meta.includeCompleted && state.kanbanData.completed.length > 0) {
            state.kanbanData.completed = []
          }
        } else {
          // Fallback: infer from completed array length
          // If completed array is empty, assume includeCompleted was false
          const hasCompleted = action.payload.kanban.completed.length > 0
          state.kanbanMeta = {
            includeCompleted: hasCompleted,
            completedCount: hasCompleted ? action.payload.kanban.completed.length : 0
          }
        }
        state.error = null
      })
      .addCase(fetchMyBoardData.rejected, (state, action) => {
        if (action.meta.requestId !== latestMyBoardRequestId) {
          return
        }
        state.loading = false
        state.error = action.payload as string
      })

    // Upload Images
    builder
      .addCase(uploadImages.pending, (state) => {
        state.error = null
      })
      .addCase(uploadImages.fulfilled, (state, action) => {
        const { issueId, images } = action.payload
        
        // Add images to current issue (slim issues from kanban may have no imageUrls array)
        if (state.currentIssue && state.currentIssue._id === issueId) {
          if (!state.currentIssue.imageUrls) state.currentIssue.imageUrls = []
          state.currentIssue.imageUrls.push(...images)
        }
        
        // Add images to issue in issues list
        const issueIndex = state.issues.findIndex(i => i._id === issueId)
        if (issueIndex !== -1) {
          if (!state.issues[issueIndex].imageUrls) state.issues[issueIndex].imageUrls = []
          state.issues[issueIndex].imageUrls.push(...images)
        }
        
        // Add images to issue in kanban data (slim issues from kanban have no imageUrls array)
        if (state.kanbanData) {
          Object.keys(state.kanbanData).forEach(column => {
            const col = column as keyof KanbanData
            const kanbanIndex = state.kanbanData![col].findIndex(i => i._id === issueId)
            if (kanbanIndex !== -1) {
              const issue = state.kanbanData![col][kanbanIndex]
              if (!issue.imageUrls) issue.imageUrls = []
              issue.imageUrls.push(...images)
            }
          })
        }
        
        state.error = null
      })
      .addCase(uploadImages.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Remove Image
    builder
      .addCase(removeImage.pending, (state) => {
        state.error = null
      })
      .addCase(removeImage.fulfilled, (state, action) => {
        const { issueId, imageId } = action.payload
        
        // Remove image from current issue (slim issues may have no imageUrls array)
        if (state.currentIssue && state.currentIssue._id === issueId) {
          const urls = state.currentIssue.imageUrls || []
          state.currentIssue.imageUrls = urls.filter(img => img._id !== imageId)
        }
        
        // Remove image from issue in issues list
        const issueIndex = state.issues.findIndex(i => i._id === issueId)
        if (issueIndex !== -1) {
          const urls = state.issues[issueIndex].imageUrls || []
          state.issues[issueIndex].imageUrls = urls.filter(img => img._id !== imageId)
        }
        
        // Remove image from issue in kanban data (slim issues have no imageUrls array)
        if (state.kanbanData) {
          Object.keys(state.kanbanData).forEach(column => {
            const col = column as keyof KanbanData
            const kanbanIndex = state.kanbanData![col].findIndex(i => i._id === issueId)
            if (kanbanIndex !== -1) {
              const urls = state.kanbanData![col][kanbanIndex].imageUrls || []
              state.kanbanData![col][kanbanIndex].imageUrls = urls.filter(img => img._id !== imageId)
            }
          })
        }
        
        state.error = null
      })
      .addCase(removeImage.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Labels
    builder
      .addCase(fetchLabels.pending, (state) => {
        state.error = null
      })
      .addCase(fetchLabels.fulfilled, (state, action) => {
        state.labels = action.payload
        state.error = null
      })
      .addCase(fetchLabels.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const {
  clearCurrentIssue,
  clearError,
  setLoading,
  updateIssueInList,
  moveIssueInKanban,
  updateIssueTimeTracking,
  addCommentToIssue,
  updateIssueInKanban,
  removeIssueFromKanban,
} = issueSlice.actions

export default issueSlice.reducer
